import {
  benefits,
  categories,
  homeShowcase,
  homeStatement,
  homeStats,
  homeVideo,
  hotlines,
  navLinks,
  openingHours,
  refreshDerivedSiteConfig,
  reviews,
  services,
  site,
  socialLinks,
} from "@/lib/siteConfig";
import { serviceContent, serviceLinks } from "@/lib/serviceContent";
import { getArabicCmsContent } from "@/cms/arabicContent";

export const pageContent = {
  home: {
    servicesHeading: {
      eyebrow: "Unsere Leistungen",
      title: "Alles an einem Ort. Für Sie.",
      subtitle: "Wählen Sie den Bereich, den Sie benötigen – wir kümmern uns persönlich um den Rest.",
    },
    benefitsHeading: {
      eyebrow: "Ihre Vorteile",
      title: "Warum Kundinnen und Kunden uns vertrauen.",
    },
    contact: {
      eyebrow: "Kontakt aufnehmen",
      title: "Wir sind für Sie da.",
      subtitle: "Schreiben Sie uns eine Nachricht – wir melden uns schnellstmöglich persönlich bei Ihnen. Oder rufen Sie direkt an.",
      phoneLabel: "Hotline",
      emailLabel: "E-Mail",
      formTitle: "Schnellanfrage",
      formSubtitle: "In wenigen Sekunden – wir rufen zurück.",
      submitLabel: "Nachricht senden",
    },
  },
  about: {
    hero: {
      eyebrow: "AZ Gruppe",
      title: "Über uns",
      subtitle: "Die AZ Gruppe ist Ihr zuverlässiger Partner für professionelle Fahrzeugservices in Essen.",
      image: "/images/about/about-1.png",
      alt: "AZ Gruppe – persönliche Beratung im Büro in Essen",
    },
    contentImage: { src: "/images/about/about-2.png", alt: "Empfangsbereich der AZ Gruppe in Essen" },
    paragraphs: [
      "Wir unterstützen Privatkunden, Autohäuser, Leasinggesellschaften und Unternehmen mit schnellen, persönlichen und zuverlässigen Lösungen rund um KFZ-Zulassung, Gutachten und weitere Serviceleistungen.",
      "Dank unserer direkten Anbindung an das Online-Zulassungssystem der Stadt Essen können viele Zulassungsvorgänge schnell, effizient und ohne lange Wartezeiten bearbeitet werden.",
      "Mit unserer eigenen Kennzeichen-Prägemaschine erhalten Sie Ihre Nummernschilder direkt vor Ort – ohne zusätzliche Wege.",
      "Ob Fahrzeugzulassung, Ummeldung, Abmeldung, Kurzzeitkennzeichen, Ausfuhrkennzeichen oder Wunschkennzeichen – wir übernehmen die Abwicklung zuverlässig und persönlich.",
    ],
    servicesHeading: "Unser Service umfasst unter anderem:",
    services: [
      "Fahrzeugzulassungen", "Ummeldungen", "Abmeldungen", "Kurzzeitkennzeichen",
      "Ausfuhrkennzeichen", "Wunschkennzeichen", "Ersatz von Fahrzeugpapieren",
      "Kennzeichenprägung direkt vor Ort",
    ],
    gallery: [
      { src: "/images/about/about-3.png", alt: "Persönliche Beratung bei der AZ Gruppe" },
      { src: "/images/about/about-4.png", alt: "AZ Gruppe Arbeitsplatz mit Markenausstattung" },
      { src: "/images/about/about-5.png", alt: "Das Team der AZ Gruppe" },
      { src: "/images/about/about-6.png", alt: "Firmensitz der AZ Gruppe" },
    ],
    cta: {
      image: "/images/about/about-cta.png",
      eyebrow: "Schnell • Kompetent • Zuverlässig",
      text: "Ein Ansprechpartner für Ihren gesamten Fahrzeugservice.",
      highlight: "Alles aus einer Hand.",
      contactButton: "Kontakt aufnehmen",
      phoneButton: "Hotline anrufen",
    },
  },
  partner: {
    hero: {
      image: "/images/home/warum-az.png",
      alt: "Beratungsteam der AZ Gruppe im Gespräch am Konferenztisch",
      eyebrow: "Partnernetzwerk",
      title: "Unsere Leistungsbereiche – aus einer Hand.",
      subtitle: "Wir sind kein reiner Vermittler. In allen Bereichen kümmern wir uns selbst um Ihre Anliegen – verlässlich, persönlich und transparent. Wählen Sie einen Bereich, um mehr zu erfahren.",
    },
    heading: {
      eyebrow: "Leistungsbereiche",
      title: "Vier Bereiche – ein Ansprechpartner.",
      subtitle: "Jeder Bereich hat seine eigene Seite mit ausführlichen Informationen, Bildern und Videos. Klicken Sie auf „Mehr erfahren“.",
    },
  },
  contact: {
    hero: {
      image: "/images/kontakt/kontakt-hero.png",
      eyebrow: "Kontakt",
      title: "Lassen Sie uns sprechen.",
      subtitle: "Ob Gutachten, Versicherung, Kfz-Zulassung oder Vertragsservice – wir sind für Sie da und beraten Sie persönlich.",
    },
    details: {
      title: "So erreichen Sie uns",
      subtitle: "Rufen Sie eine unserer Hotlines an, schreiben Sie uns eine E-Mail oder nutzen Sie das Formular. Wir freuen uns auf Ihre Anfrage.",
      emailLabel: "E-Mail", addressLabel: "Adresse", hoursLabel: "Öffnungszeiten",
    },
    social: { title: "So erreichen Sie uns", subtitle: "Wählen Sie den passenden Kontaktweg. Wir sind gerne für Sie da." },
    form: { title: "Anfrage senden", subtitle: "Füllen Sie das Formular aus – wir melden uns schnellstmöglich persönlich bei Ihnen.", submitLabel: "Anfrage senden" },
  },
};

export const commonContent = {
  hero: {
    headline: "Ihr Partner von A bis Z.",
    subtitle: "Ein System für Versicherungen, Gutachten, Verträge und Fahrzeugservice – zuverlässig, persönlich und aus einer Hand.",
    phoneButton: "Hotline anrufen",
    contactButton: "Kontakt aufnehmen",
  },
  hotlines: {
    eyebrow: "Direkter Draht",
    title: "Ihre Hotlines im Überblick",
    subtitle: "Erreichen Sie den passenden Bereich direkt – wir sind persönlich für Sie da.",
  },
  reviews: {
    eyebrow: "Google Bewertungen",
    title: "Das sagen unsere Kunden",
    closing: "Vielen Dank für Ihr Vertrauen.",
    background: "/images/home/reviews-bg.png",
  },
  footer: {
    description: "Ein System für Versicherungen, Gutachten, Verträge und Fahrzeugservice – zuverlässig, persönlich und aus einer Hand.",
    navigationTitle: "Navigation",
    servicesTitle: "Leistungen",
    contactTitle: "Kontakt",
    socialTitle: "Folgen Sie uns",
    rights: "Alle Rechte vorbehalten.",
    developer: "Entwickelt von BAYAN NETZ",
  },
  navbar: {
    phoneButton: "Hotline anrufen", contactButton: "Kontakt aufnehmen",
    socialTitle: "Folgen Sie uns", emailButton: "E-Mail senden",
  },
  form: {
    nameLabel: "Name", namePlaceholder: "Ihr Name",
    emailLabel: "E-Mail", emailPlaceholder: "ihre@email.de",
    phoneLabel: "Telefon", optionalLabel: "(optional)", phonePlaceholder: "+49 …",
    categoryLabel: "Auswahlbereich", categoryPlaceholder: "Bitte wählen",
    messageLabel: "Nachricht", messagePlaceholder: "Wie können wir Ihnen helfen?",
    sendingLabel: "Wird gesendet …",
    consent: "Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben zur Bearbeitung Ihrer Anfrage zu.",
    successTitle: "Vielen Dank für Ihre Anfrage!",
    successText: "Ihre Nachricht ist bei uns eingegangen. Unser Team meldet sich schnellstmöglich persönlich bei Ihnen.",
    resetButton: "Weitere Anfrage senden",
    nameError: "Bitte geben Sie Ihren Namen ein.",
    emailError: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    messageError: "Bitte schreiben Sie uns eine kurze Nachricht.",
  },
  service: {
    detailEyebrow: "Im Detail",
    phoneButton: "Hotline anrufen",
    contactButton: "Kontakt aufnehmen",
    emailButton: "E-Mail senden",
    submitLabel: "Anfrage senden",
  },
};

const editableRuntime = {
  global: { site, hotlines, openingHours, navLinks, categories, services, benefits, reviews, socialLinks },
  home: { homeStatement, homeShowcase, homeVideo, homeStats, page: pageContent.home },
  pages: { about: pageContent.about, partner: pageContent.partner, contact: pageContent.contact },
  servicePages: serviceContent,
  common: commonContent,
};

const clone = (value) => JSON.parse(JSON.stringify(value));
const originalDefaults = clone(editableRuntime);

const mergeFixedShape = (target, incoming) => {
  if (!incoming || typeof incoming !== "object") return;
  Object.keys(target).forEach((key) => {
    if (!(key in incoming)) return;
    const current = target[key];
    const next = incoming[key];
    if (Array.isArray(current)) {
      if (!Array.isArray(next)) return;
      current.forEach((item, index) => {
        if (index >= next.length) return;
        if (item && typeof item === "object") mergeFixedShape(item, next[index]);
        else if (typeof next[index] === typeof item) current[index] = next[index];
      });
    } else if (current && typeof current === "object") {
      mergeFixedShape(current, next);
    } else if (typeof next === typeof current) {
      target[key] = next;
    }
  });
};

export const getDefaultCmsContent = () => ({
  de: clone(originalDefaults),
  ar: getArabicCmsContent(originalDefaults),
});

export const normalizeCmsContent = (incoming = {}) => {
  const normalized = getDefaultCmsContent();
  if (incoming?.de || incoming?.ar) {
    mergeFixedShape(normalized.de, incoming.de);
    mergeFixedShape(normalized.ar, incoming.ar);
    return normalized;
  }

  // Content saved before bilingual support is German and is preserved there.
  mergeFixedShape(normalized.de, incoming);
  return normalized;
};

export const applyCmsContent = (incoming = {}, locale = "de") => {
  const localized = normalizeCmsContent(incoming)[locale === "ar" ? "ar" : "de"];
  mergeFixedShape(editableRuntime, originalDefaults);
  mergeFixedShape(editableRuntime, localized);
  refreshDerivedSiteConfig();
  serviceLinks.forEach((link, index) => {
    if (services[index]) link.label = services[index].title;
  });
  const whatsappText = locale === "ar"
    ? "مرحباً، زرت موقعكم وأرغب في الحصول على مزيد من المعلومات."
    : "Guten Tag, ich habe Ihre Website besucht und möchte gerne weitere Informationen erhalten.";
  socialLinks.forEach((link) => {
    if (link.id === "whatsapp-zulassung") link.href = `https://wa.me/4915129895163?text=${encodeURIComponent(whatsappText)}`;
    if (link.id === "whatsapp-versicherung") link.href = `https://wa.me/4917643261918?text=${encodeURIComponent(whatsappText)}`;
  });
};

export const isImageField = (path, value) =>
  typeof value === "string" &&
  (/(^|\.)(image|src|poster|background)$/i.test(path) || /\.(png|jpe?g|webp|avif|gif)(\?|$)/i.test(value));

export const shouldEditField = (path, value) => {
  if (typeof value !== "string") return false;
  // Only the public social-network destinations are editable. The remaining
  // href fields are structural navigation/contact links managed by the app.
  if (/^global\.socialLinks\.(0|1|2)\.href$/i.test(path)) return true;
  return (
    !/(^|\.)common\.footer\.developer$/i.test(path) &&
    !/homeVideo\.src$/i.test(path) &&
    !/(^|\.)(theme|lines)(\.|$)/i.test(path) &&
    !/(^|\.)(id|icon|testid|to|tel|value|slug|category|href)$/i.test(path)
  );
};
